import React, { useCallback, useContext, useMemo, useRef } from "react";
import {
  Platform,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import { useTheme } from "react-native-elements";
import { PlayerContext, PlayerContextValue } from "../../backend/PlayerContext";
import {
  addActionToQuickbar,
  isInQuickbar,
  isQuickbarFull,
} from "../../backend/Quickbar";

import { Entypo, Ionicons } from "@expo/vector-icons";
import { getUnlockedElements } from "../../backend/spells/ElementsAndSchools";
import { Spell, SpellActionBase } from "../../backend/spells/Spell";
import {
  addFavoriteSpell,
  isInFavoriteSpells,
  removeFromFavoriteSpells,
} from "../../backend/spells/Spells";
import { formatNumber } from "../../utils/FormattingUtils";
import { ActiveMark } from "./ActiveMark";
import { Button } from "./Button";
import { Markdown } from "./Markdown";
import {
  DescriptionViewer,
  disabledColorByTheme,
  elementColorByTheme,
} from "./Miscellaneous";
import { Text } from "./Text";
import { TooltipPositionType, TooltipWrapper } from "./Tooltip";
import { GameIcon } from "./icons/GameIcon";

type SpellViewProps = {
  spell: Spell;
  showAddToQuickbar?: boolean;
  collapsed?: boolean;
  toggleCollapsed?: () => void;
  isMinimal?: boolean;
};

function SpellViewMemoized(
  props: SpellViewProps & { playerContext: PlayerContextValue },
) {
  const playerContext = props.playerContext;
  const playerContextRef = useRef(props.playerContext);
  const spell = props.spell;
  const showAddToQuickbar = props.showAddToQuickbar || false;

  const descriptionContainer = useMemo(() => {
    const descriptionContents = `${spell.getDisplayDescription(
      playerContext,
    )} **${spell.getDisplayEffect(playerContext)}**`;
    return (
      <View style={styles.descriptionTextContainer}>
        <DescriptionViewer style={{ flex: 1 }}>
          {descriptionContents}
        </DescriptionViewer>
      </View>
    );
  }, [
    (!props.collapsed || Platform.OS === "web") &&
      spell.getDisplayDescription(playerContext),
    (!props.collapsed || Platform.OS === "web") &&
      spell.getDisplayEffect(playerContext),
  ]);

  const theme = useTheme();

  let spellElement = spell.getElement();
  if (
    spellElement &&
    !getUnlockedElements(playerContext).includes(spellElement)
  ) {
    spellElement = undefined;
  }
  const elementText = useMemo(() => {
    return (
      <View>
        {spellElement != null && (
          <View style={styles.elementContainer}>
            <Markdown>Element:</Markdown>
            <View
              style={[
                styles.elementTextContainer,
                {
                  backgroundColor: elementColorByTheme(
                    theme.theme.mode,
                    spellElement,
                  ),
                },
              ]}
            >
              <Text
                style={[
                  spellElement === playerContext.primaryElement &&
                    styles.primaryElementText,
                  styles.spellElementBadgeText,
                ]}
              >
                {spellElement}
              </Text>
            </View>
          </View>
        )}
      </View>
    );
  }, [spellElement, theme]);

  const cooldownContainer = useMemo(() => {
    const cooldown = (spell as SpellActionBase).getCooldown(playerContext);
    const cooldownText = `${formatNumber(cooldown, {
      showDecimals: true,
    })} sec`;
    return (
      <View>
        {cooldown > 0 && (
          <View style={styles.costContainer}>
            <Markdown>Cooldown:</Markdown>
            <Markdown containerStyle={{ flex: null }}>{cooldownText}</Markdown>
          </View>
        )}
      </View>
    );
  }, [
    (!props.collapsed || Platform.OS === "web") &&
      (spell as SpellActionBase).getCooldown(playerContext),
  ]);

  const toggleFavorite = useCallback(() => {
    playerContextRef.current.apply((state) =>
      isInFavoriteSpells(state, spell)
        ? removeFromFavoriteSpells(state, spell)
        : addFavoriteSpell(state, spell),
    );
  }, []);

  const favoritesButton = useMemo(
    () => (
      <Ionicons
        accessibilityLabel={
          isInFavoriteSpells(playerContext, spell)
            ? "Remove from Favorites"
            : "Add to Favorites"
        }
        onPress={toggleFavorite}
        name={
          isInFavoriteSpells(playerContext, spell) ? "heart" : "heart-outline"
        }
        style={[styles.expandContainer]}
        color={theme.theme.colors.black}
        size={32}
      />
    ),
    [
      !props.showAddToQuickbar && isInFavoriteSpells(playerContext, spell),
      toggleFavorite,
      theme,
    ],
  );

  const disabled = !spell.isEnabled(playerContext);
  const buttonTitleStyles = useMemo(
    () => [
      {
        color: "white",
      },
      styles.buttonTitle,
      disabled && {
        color: disabledColorByTheme(theme.theme),
      },
      styles.smallButtonTitle,
    ],
    [theme?.theme?.colors?.disabled, disabled],
  );
  const spellDisplayName = spell.getDisplayName(playerContext);
  const spellCost = spell.getCost(playerContext)?.resources?.Mana ?? 0;
  const isActive = spell.isActive(playerContext);
  const buttonOnPress = useCallback(
    () =>
      playerContextRef.current.apply(
        spell.doAction.bind(spell, { isAutomatic: false }),
      ),
    [],
  );
  const button = useMemo(
    () => (
      <TooltipWrapper
        tooltip={
          props.collapsed ? (
            <View>
              {descriptionContainer}
              {elementText}
              {cooldownContainer}
            </View>
          ) : undefined
        }
        tooltipPosition={
          showAddToQuickbar
            ? TooltipPositionType.RIGHT_OF_ELEMENT
            : TooltipPositionType.LEFT_OF_ELEMENT
        }
        tooltipId={spell.getName()}
      >
        <Button
          key={spell.getName()}
          enableAutoRepress={true}
          onPress={buttonOnPress}
          title={
            <View style={styles.spellButtonTitleContainer}>
              <View style={styles.spellButtonTitleOnlyContainer}>
                {isActive && <ActiveMark disabled={disabled} />}
                <View style={styles.spellButtonTitleTitleOnlyContainer}>
                  <Text
                    style={buttonTitleStyles}
                    numberOfLines={props.isMinimal ? 1 : undefined}
                  >
                    {spellDisplayName}
                  </Text>
                </View>
              </View>
              <View style={styles.spellButtonCostContainer}>
                <Text style={buttonTitleStyles} doNotScaleFonts={true}>
                  {formatNumber(spellCost, { showDecimals: false })}
                </Text>
                <GameIcon icon="mana" style={styles.spellManaIcon} />
              </View>
            </View>
          }
          titleStyle={styles.smallButtonTitle}
          containerStyle={[styles.buttonContainer, styles.buttonContainerSmall]}
          disabled={disabled}
        />
      </TooltipWrapper>
    ),
    [
      spell.getDisplayName(playerContext),
      buttonOnPress,
      buttonTitleStyles,
      spellDisplayName,
      spellCost,
      disabled,
      isActive,
      descriptionContainer,
      elementText,
      cooldownContainer,
      props.collapsed,
      props.isMinimal,
      showAddToQuickbar,
    ],
  );

  const addToQuickbarButton = useMemo(
    () => (
      <Button
        onPress={() =>
          playerContextRef.current.apply((state) =>
            addActionToQuickbar(state, spell),
          )
        }
        title="Add to Quickbar"
        titleStyle={styles.smallButtonTitle}
        containerStyle={[styles.quickbarButtonContainer]}
        disabled={
          isInQuickbar(playerContext, {
            type: "action",
            name: spell.getName(),
          }) || isQuickbarFull(playerContext)
        }
      />
    ),
    [
      isInQuickbar(playerContext, {
        type: "action",
        name: spell.getName(),
      }),
      isQuickbarFull(playerContext),
    ],
  );

  return useMemo(() => {
    if (props.isMinimal) {
      return <View style={styles.buttonContainerView}>{button}</View>;
    }
    return (
      <View style={styles.actionContainer} key={spell.getName()}>
        <View style={styles.buttonRowContainer}>
          <View style={styles.buttonContainerView}>{button}</View>
          <TouchableWithoutFeedback
            accessibilityLabel={props.collapsed ? "Expand" : "Collapse"}
            onPress={props.toggleCollapsed}
          >
            <View style={styles.expandContainer}>
              <Entypo
                name={props.collapsed ? "chevron-right" : "chevron-down"}
                size={24}
                color={theme.theme.colors.black}
              />
            </View>
          </TouchableWithoutFeedback>
        </View>
        {(!props.collapsed || !props.showAddToQuickbar) && (
          <View style={props.collapsed && { display: "none" }}>
            <View style={styles.descriptionRow}>
              {descriptionContainer}
              {!showAddToQuickbar && favoritesButton}
              {showAddToQuickbar && addToQuickbarButton}
            </View>
            {elementText}
            {cooldownContainer}
          </View>
        )}
      </View>
    );
  }, [
    spell.getName(),
    !props.collapsed && cooldownContainer,
    !props.collapsed && descriptionContainer,
    showAddToQuickbar,
    showAddToQuickbar && addToQuickbarButton,
    showAddToQuickbar && JSON.stringify(playerContext.quickbar),
    props.collapsed,
    theme,
    toggleFavorite,
    props.isMinimal,
    props.toggleCollapsed,
    favoritesButton,
    button,
  ]);
}

export function SpellView(props: SpellViewProps & { isVisible?: boolean }) {
  const playerContext = useContext(PlayerContext);

  return useMemo(
    () => <SpellViewMemoized playerContext={playerContext} {...props} />,
    [
      props.spell.getName(),
      props.collapsed,
      props.showAddToQuickbar,
      props.toggleCollapsed,
      props.isVisible !== false ? playerContext : false,
    ],
  );
}

const styles = StyleSheet.create({
  actionContainer: { marginVertical: 8 },
  buttonRowContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  quickbarButtonContainer: {
    marginTop: 4,
    marginLeft: 8,
  },
  expandContainer: {
    width: 40,
    alignSelf: "stretch",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  costContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginTop: 4,
  },
  elementContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 4,
  },
  descriptionTextContainer: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    paddingRight: 4,
  },
  buttonTitle: {
    fontSize: 16,
    textAlign: "center",
    paddingVertical: 1,
    ...Platform.select({
      android: {
        fontFamily: "sans-serif-medium",
      },
      default: {
        fontSize: 18,
      },
    }),
  },
  smallButtonTitle: {
    fontSize: 12,
  },
  buttonContainerView: {
    flexDirection: "row",
    flex: 1,
  },
  buttonContainer: {
    flex: 1,
  },
  buttonContainerSmall: {
    marginRight: 0,
  },
  disabledActionText: {
    color: "#aaa",
  },
  capTooLowText: {
    color: "#d99",
  },
  turnOnRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 8,
  },
  turnOnQuantityContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  plusMinusContainer: {
    marginLeft: 8,
  },
  spellButtonTitleContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  spellButtonTitleOnlyContainer: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  spellButtonTitleTitleOnlyContainer: {
    flex: 1,
  },
  spellButtonCostContainer: {
    flexDirection: "row",
    width: 50,
    justifyContent: "flex-end",
  },
  spellManaIcon: {
    height: 16,
    width: 16,
    justifyContent: "flex-end",
    alignItems: "center",
    top: 1,
    marginLeft: 2,
  },
  descriptionRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginTop: 8,
  },
  elementTextContainer: {
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  primaryElementText: {
    fontWeight: "bold",
  },
  spellElementBadgeText: {
    color: "white",
  },
});
